import React from 'react'
import { Box, Typography, TextField, Select, MenuItem, FormControl, Tooltip } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { isolateNumber } from '@/utils/numberDirection'

interface DetailRowProps {
  label: string
  value: string | number
  isHighlighted?: boolean
  section?: boolean
  // Edit mode props
  isEditMode?: boolean
  editType?: 'text' | 'dropdown' | 'number'
  dropdownOptions?: { value: string | number; label: string }[]
  onChange?: (newValue: string | number) => void
  fieldKey?: string
  // Disable editing for empty values
  disableIfEmpty?: boolean
  isEditable?: boolean
}

const DetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  section,
  isEditMode = false,
  editType = 'text',
  dropdownOptions = [],
  onChange,
  disableIfEmpty = true,
  isEditable = true,
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const LABEL_COLOR = isDarkMode ? '#B0B0B0' : '#C0C2D6'
  const VALUE_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
  const BORDER_COLOR = isDarkMode ? '#374151' : '#f5f5f5'
  const INPUT_BG = isDarkMode ? '#2d3d55' : '#F5F7FA'
  const INPUT_BORDER = isDarkMode ? '#4B5563' : '#E4E7EB'

  // Check if value is empty (including '-' placeholder)
  const isEmpty = value === '' || value === null || value === undefined ||
                  value === '-' || value === '—' ||
                  (typeof value === 'string' && value.trim() === '') ||
                  (typeof value === 'string' && value.trim() === '-') ||
                  (typeof value === 'number' && isNaN(value))

  // Disable field if:
  // 1. It's empty and disableIfEmpty is true
  // 2. It's explicitly marked as not editable
  const isDisabled = isEditMode && (
    (disableIfEmpty && isEmpty) || 
    !isEditable
  )

  const handleChange = (newValue: string | number) => {
    if (onChange && isEditable) {
      onChange(newValue)
    }
  }

  const renderValue = () => {
    if (!isEditMode || !isEditable) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: VALUE_COLOR,
            fontSize: '14px',
            fontWeight: section ? 300 : 500,
            textAlign: 'right',
            lineHeight: 1.2,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            fontFamily: 'Rubik, sans-serif',
            flex: 1,
          }}
        >
          {isolateNumber(value)}
        </Typography>
      )
    }

    // Edit mode - render input or dropdown
    if (editType === 'dropdown') {
      return (
        <FormControl size="small" sx={{ flex: 1, maxWidth: 170 }}>
          <Select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            disabled={isDisabled}
            sx={{
              bgcolor: INPUT_BG,
              border: `1px solid ${INPUT_BORDER}`,
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'Rubik, sans-serif',
              color: VALUE_COLOR,
              direction: 'rtl',
              height: 28,
              '& .MuiSelect-select': {
                py: 0.25,
                px: 1,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSvgIcon-root': {
                color: VALUE_COLOR,
                left: 2,
                right: 'auto',
                fontSize: 16,
              },
            }}
          >
            {dropdownOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    }

    // Text or number input
    return (
      <TextField
        type={editType === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        size="small"
        variant="outlined"
        disabled={isDisabled}
        placeholder={isDisabled ? 'אין נתונים' : ''}
        sx={{
          flex: 1,
          maxWidth: 150,
          '& .MuiInputBase-root': {
            bgcolor: INPUT_BG,
            border: `1px solid ${INPUT_BORDER}`,
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: 'Rubik, sans-serif',
            color: VALUE_COLOR,
            direction: 'rtl',
            height: 28,
          },
          '& .MuiInputBase-input': {
            py: 0.25,
            px: 1,
            textAlign: 'right',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: section ? 'column' : 'row',
        py: 0.3,
        direction: 'rtl',
        alignItems: section ? 'flex-start' : 'center',
        gap: 0.5,
        minHeight: 28,
        opacity: isDisabled ? 0.4 : 1,
        cursor: isDisabled ? 'not-allowed' : 'default',
        ...(section && {
          borderTop: `2px solid ${BORDER_COLOR}`,
          borderRadius: '3px',
          textAlign: 'right',
          mt: 0.5,
        }),
      }}
      title={isDisabled ? 'אין נתונים לעריכה' : ''}
    >
      <Tooltip title={label} disableHoverListener={label.length < 15} arrow>
        <Typography
          variant="body2"
          noWrap
          sx={{
            color: LABEL_COLOR,
            fontSize: '12px',
            fontWeight: 300,
            textAlign: 'right',
            fontFamily: 'Rubik, sans-serif',
            minWidth: section ? 'auto' : 70,
            maxWidth: section ? 'auto' : '45%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
      </Tooltip>

      {renderValue()}
    </Box>
  )
}

export default DetailRow
